/**
 * NetIdentity - shared network object identity helpers.
 */

import { shortUUID } from '../utils/uuid.js';

import { NetRuntime } from './NetRuntime.js';

import type { ClientId, DeviceId, NetId, ObjectId } from './interface.js';
import type { SerializedData } from '../utils/serialization/types.js';
import type { Vector3Like } from 'three';

export interface INetObject {
  /**
   * Determines if this object is relevant for network replication to a specific client.
   * @param clientId - Optional client ID to check relevance for (for per-client filtering)
   * @param clientData - Optional client data (e.g., position, view frustum) for relevance checks
   * @returns true if this object should be replicated
   */
  isNetRelevantFor(clientId?: string, clientData?: { position?: Vector3Like;[key: string]: any }): boolean;
  getNetObjectId(): ObjectId;
  getNetClientId(): ClientId;
  getNetDeviceId(): DeviceId;
  getNetConstructionData(): SerializedData | null;
}

export type NetIdStruct = {
  deviceId: DeviceId;
  clientId: ClientId;
  objectId: ObjectId;
}

export function makeNetId(struct: NetIdStruct): NetId {
  return `${struct.deviceId}:${struct.clientId}:${struct.objectId}`;
}

export function parseNetId(netId: NetId): NetIdStruct {
  const [deviceId, clientId, objectId] = netId.split(':');
  return { deviceId, clientId, objectId };
}

// Constants for static object NetIDs
const STATIC_DEVICE_ID: DeviceId = 'Device\'world\'';
const STATIC_CLIENT_ID: ClientId = 'Client\'world\'';

/**
 * Generate a deterministic NetID for a static object (Tier 1/2) based on its UUID
 * Both server and client generate the same NetID for matching
 * @param uuid - The actor's stable UUID from the world file
 * @param className - The actor's class name
 * @returns A deterministic NetID
 */
export function makeStaticNetId(uuid: string, className: string): NetId {
  const objectId = `${className}'${uuid}'`;
  return makeNetId({ deviceId: STATIC_DEVICE_ID, clientId: STATIC_CLIENT_ID, objectId });
}

/**
 * Check if a NetID represents a static object (Tier 1/2)
 * @param netId - The NetID to check
 * @returns true if this is a static object NetID
 */
export function isStaticNetId(netId: NetId): boolean {
  const { deviceId, clientId } = parseNetId(netId);
  return deviceId === STATIC_DEVICE_ID && clientId === STATIC_CLIENT_ID;
}

let headlessDeviceId: DeviceId | null = null;

export function getDeviceId(): DeviceId {
  // Server doesn't have localStorage
  if (NetRuntime.isDedicatedServer()) {
    return 'Device\'server\'';
  }

  const storage = globalThis.localStorage;
  if (storage) {
    let deviceId = localStorage.getItem('genesysDeviceId');
    if (!deviceId) {
      deviceId = `Device'${shortUUID()}'`;
      localStorage.setItem('genesysDeviceId', deviceId);
    }
    return deviceId;
  }

  headlessDeviceId ??= `Device'${shortUUID()}'`;
  return headlessDeviceId;
}
